import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Grid,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import { CheckCircle, Error as ErrorIcon, Search as SearchIcon } from "@mui/icons-material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";

const primaryButtonSx = {
  background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
  color: "#fff",
  WebkitTextFillColor: "#fff",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
    opacity: 0.9,
  },
  "&.Mui-disabled": {
    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
    color: "#fff",
    WebkitTextFillColor: "#fff",
    opacity: 0.45,
  },
};

const toDate = (x) => {
  if (!x) return null;
  if (x.toDate) return x.toDate();
  try {
    return new Date(x);
  } catch {
    return null;
  }
};

const num = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

const approxEq = (a, b, eps = 0.01) => Math.abs(num(a) - num(b)) <= eps;

const formatMoney = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num(amount));

const txnAmount = (t) => num(t.total_amount ?? t.withdraw_amount ?? t.amount ?? 0);
const ledgerAmount = (l) => num(l.new_txn_amount ?? l.amount ?? 0);

const bestTxnTime = (t) => toDate(t.request_approval_time) || toDate(t.date) || null;
const bestLedgerTime = (l) => toDate(l.date) || toDate(l.conclusion_date) || toDate(l.request_date) || null;

function withinDays(a, b, days = 3) {
  if (!a || !b) return false;
  return Math.abs(a.getTime() - b.getTime()) <= days * 24 * 60 * 60 * 1000;
}

function findBestLedgerForTxn(txn, ledgers, wantedTypes) {
  const amount = txnAmount(txn);
  const tTime = bestTxnTime(txn);

  // 1) direct id/doc_id linkage
  const txnIds = new Set([txn.id, txn.doc_id].filter(Boolean).map(String));
  const direct = ledgers.find(
    (l) =>
      wantedTypes.includes(l.transection_type) &&
      (txnIds.has(String(l.doc_id ?? "")) || txnIds.has(String(l.id ?? "")))
  );
  if (direct) return { match: direct, method: "direct_id" };

  // 2) amount + closest date (within window)
  const candidates = ledgers
    .filter((l) => wantedTypes.includes(l.transection_type))
    .filter((l) => approxEq(ledgerAmount(l), amount));

  if (candidates.length === 0) return { match: null, method: "none" };

  if (!tTime) return { match: candidates[0], method: "amount_only" };

  let best = null;
  let bestDiff = Infinity;
  for (const l of candidates) {
    const lTime = bestLedgerTime(l);
    if (!lTime) continue;
    const diff = Math.abs(lTime.getTime() - tTime.getTime());
    if (diff < bestDiff) {
      bestDiff = diff;
      best = l;
    }
  }

  if (!best) return { match: candidates[0], method: "amount_only" };
  if (!withinDays(bestLedgerTime(best), tTime, 3)) return { match: best, method: "amount_date_far" };
  return { match: best, method: "amount_date" };
}

function findBestTxnForLedger(ledger, txns, wantedTxnTypes) {
  const amount = ledgerAmount(ledger);
  const lTime = bestLedgerTime(ledger);

  // 1) direct id/doc_id linkage
  const ledgerIds = new Set([ledger.id, ledger.doc_id].filter(Boolean).map(String));
  const direct = txns.find(
    (t) =>
      wantedTxnTypes.includes(t.request_type) &&
      (ledgerIds.has(String(t.id ?? "")) || ledgerIds.has(String(t.doc_id ?? "")))
  );
  if (direct) return { match: direct, method: "direct_id" };

  // 2) amount + closest approval/date
  const candidates = txns
    .filter((t) => wantedTxnTypes.includes(t.request_type))
    .filter((t) => approxEq(txnAmount(t), amount));
  if (candidates.length === 0) return { match: null, method: "none" };

  if (!lTime) return { match: candidates[0], method: "amount_only" };

  let best = null;
  let bestDiff = Infinity;
  for (const t of candidates) {
    const tTime = bestTxnTime(t);
    if (!tTime) continue;
    const diff = Math.abs(tTime.getTime() - lTime.getTime());
    if (diff < bestDiff) {
      bestDiff = diff;
      best = t;
    }
  }
  if (!best) return { match: candidates[0], method: "amount_only" };
  if (!withinDays(bestTxnTime(best), lTime, 3)) return { match: best, method: "amount_date_far" };
  return { match: best, method: "amount_date" };
}

export default function ClientReconciliationTool() {
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const q = query(collection(DATABASE, "users"), where("role", "==", 1));
        const snap = await getDocs(q);
        const users = snap.docs
          .map((d) => ({
            id: d.id,
            email: d.data().email || "",
            name: (d.data().full_name || d.data().name || "").trim() || d.data().email || "",
          }))
          .filter((u) => u.email)
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        setUserOptions(users);
      } catch (e) {
        setError(e.message || "Failed to load users");
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, []);

  const run = async () => {
    if (!selectedUser?.id) return;
    setRunning(true);
    setError(null);
    setReport(null);
    try {
      // Load user doc to get net_balance
      const userSnap = await getDocs(query(collection(DATABASE, "users"), where("email", "==", selectedUser.email)));
      const userDoc = userSnap.docs[0];
      const userData = userDoc?.data() || {};

      const [ledSnap, txSnap] = await Promise.all([
        getDocs(query(collection(DATABASE, "ledgers"), where("user_id", "==", userDoc.id))),
        getDocs(query(collection(DATABASE, "transection_requests"), where("user_id", "==", userDoc.id))),
      ]);

      const ledgers = ledSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .map((l) => ({ ...l, _dt: bestLedgerTime(l) }))
        .sort((a, b) => (a._dt?.getTime?.() || 0) - (b._dt?.getTime?.() || 0));

      const txns = txSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .map((t) => ({ ...t, _dt: bestTxnTime(t) }))
        .sort((a, b) => (a._dt?.getTime?.() || 0) - (b._dt?.getTime?.() || 0));

      // Totals
      const ledDep = ledgers.filter((l) => l.transection_type === 0);
      const ledWith = ledgers.filter((l) => l.transection_type === 1 || l.transection_type === 2);
      const ledPerf = ledgers.filter((l) => l.transection_type === 3);

      const txDepApproved = txns.filter((t) => t.request_type === 0 && t.request_status === 2);
      const txWithPaid = txns.filter((t) => t.request_type === 1 && t.request_status === 5);

      // Matching expectations: deposits approved => ledger type 0; withdrawals paid => ledger type 2
      const missingLedgerForTxn = [];
      for (const t of [...txDepApproved, ...txWithPaid]) {
        const wantedLedgerTypes = t.request_type === 0 ? [0] : [2, 1];
        const { match, method } = findBestLedgerForTxn(t, ledgers, wantedLedgerTypes);
        if (!match) {
          missingLedgerForTxn.push({
            txnId: t.id,
            type: t.request_type,
            status: t.request_status,
            amount: txnAmount(t),
            when: t._dt?.toISOString?.() || null,
            notes: t.notes || null,
            reason: "no_ledger_match",
          });
        } else if (method === "amount_date_far") {
          missingLedgerForTxn.push({
            txnId: t.id,
            type: t.request_type,
            status: t.request_status,
            amount: txnAmount(t),
            when: t._dt?.toISOString?.() || null,
            notes: t.notes || null,
            reason: `weak_match:${method}`,
            matchedLedgerId: match.id,
          });
        }
      }

      const orphanLedger = [];
      for (const l of [...ledDep, ...ledWith]) {
        const wantedTxnTypes = l.transection_type === 0 ? [0] : [1];
        const { match, method } = findBestTxnForLedger(l, txns, wantedTxnTypes);
        if (!match) {
          orphanLedger.push({
            ledgerId: l.id,
            type: l.transection_type,
            amount: ledgerAmount(l),
            when: l._dt?.toISOString?.() || null,
            doc_id: l.doc_id || null,
            reason: "no_txn_match",
          });
        } else if (method === "amount_date_far") {
          orphanLedger.push({
            ledgerId: l.id,
            type: l.transection_type,
            amount: ledgerAmount(l),
            when: l._dt?.toISOString?.() || null,
            doc_id: l.doc_id || null,
            reason: `weak_match:${method}`,
            matchedTxnId: match.id,
          });
        }
      }

      // Duplicate ledgers by amount + previous/current balance
      const dupGroups = new Map();
      for (const l of ledWith) {
        const a = ledgerAmount(l);
        const prev = num(l.previous_balance ?? 0);
        const curr = num(l.current_balance ?? l.balance ?? 0);
        const key = `${a}|${prev}|${curr}|${l.transection_type}`;
        if (!dupGroups.has(key)) dupGroups.set(key, []);
        dupGroups.get(key).push(l);
      }
      const duplicates = Array.from(dupGroups.entries())
        .filter(([, arr]) => arr.length > 1)
        .map(([key, arr]) => ({
          key,
          count: arr.length,
          rows: arr
            .slice()
            .sort((a, b) => (a._dt?.getTime?.() || 0) - (b._dt?.getTime?.() || 0))
            .map((l) => ({
              ledgerId: l.id,
              doc_id: l.doc_id || null,
              when: l._dt?.toISOString?.() || null,
              amount: ledgerAmount(l),
              prev: num(l.previous_balance ?? 0),
              curr: num(l.current_balance ?? l.balance ?? 0),
              status: l.request_status ?? null,
              migrated_from: l._migrated_from ?? null,
            })),
        }));

      // Balance sanity
      const lastLedger = ledgers.length ? ledgers[ledgers.length - 1] : null;
      const lastLedgerBalance = lastLedger ? num(lastLedger.current_balance ?? lastLedger.balance ?? lastLedger.displayBalance ?? 0) : 0;
      const userNetBalance = num(userData.net_balance ?? 0);
      const balanceMatches = approxEq(lastLedgerBalance, userNetBalance, 0.01);

      setReport({
        user: { id: userDoc.id, email: userData.email, name: userData.full_name || userData.name || selectedUser.name },
        counts: { ledgers: ledgers.length, txns: txns.length },
        totals: {
          ledger: {
            deposits: { count: ledDep.length, sum: ledDep.reduce((s, l) => s + ledgerAmount(l), 0) },
            withdrawals: { count: ledWith.length, sum: ledWith.reduce((s, l) => s + ledgerAmount(l), 0) },
            performance: { count: ledPerf.length, sum: ledPerf.reduce((s, l) => s + ledgerAmount(l), 0) },
          },
          txn: {
            depositsApproved: { count: txDepApproved.length, sum: txDepApproved.reduce((s, t) => s + txnAmount(t), 0) },
            withdrawalsPaid: { count: txWithPaid.length, sum: txWithPaid.reduce((s, t) => s + txnAmount(t), 0) },
          },
        },
        balance: {
          userNetBalance,
          lastLedgerBalance,
          matches: balanceMatches,
          lastLedgerId: lastLedger?.id || null,
          lastLedgerDate: lastLedger?._dt?.toISOString?.() || null,
        },
        issues: {
          missingLedgerForTxn,
          orphanLedger,
          duplicates,
        },
      });
    } catch (e) {
      setError(e.message || "Failed to run reconciliation");
    } finally {
      setRunning(false);
    }
  };

  const issueCounts = useMemo(() => {
    if (!report) return null;
    return {
      missingLedgerForTxn: report.issues.missingLedgerForTxn.length,
      orphanLedger: report.issues.orphanLedger.length,
      duplicates: report.issues.duplicates.length,
    };
  }, [report]);

  return (
    <Box>
      <Typography variant="h6" sx={{ color: "#2af598", mb: 2 }}>
        Client Reconciliation (read-only)
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Reads Rowy only. Compares `ledgers` vs `transection_requests` for one client, flags missing links, likely duplicates, and verifies `users.net_balance` against the last ledger.
      </Alert>

      <Card sx={{ mb: 2, bgcolor: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={userOptions}
                loading={loadingUsers}
                value={selectedUser}
                onChange={(_, v) => {
                  setSelectedUser(v);
                  setReport(null);
                  setError(null);
                }}
                getOptionLabel={(opt) => (opt.name ? `${opt.name} (${opt.email})` : opt.email || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client (name A–Z)"
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { color: "#eee" }, "& label": { color: "#aaa" } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                startIcon={running ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={run}
                disabled={!selectedUser || running}
                sx={primaryButtonSx}
              >
                Run reconciliation
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report && (
        <>
          <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
                Summary
              </Typography>
              <Typography variant="body2" sx={{ color: "#aaa", mb: 2 }}>
                {report.user.name} · {report.user.email} · ledgers: {report.counts.ledgers} · transactions: {report.counts.txns}
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Chip
                  icon={report.balance.matches ? <CheckCircle /> : <ErrorIcon />}
                  label={`Balance: ${formatMoney(report.balance.userNetBalance)} (user) vs ${formatMoney(report.balance.lastLedgerBalance)} (last ledger)`}
                  sx={{
                    bgcolor: report.balance.matches ? "rgba(42,245,152,0.2)" : "rgba(245,42,45,0.2)",
                    color: report.balance.matches ? "#2af598" : "#f52a2d",
                  }}
                />
                <Chip label={`Missing ledger for txn: ${issueCounts.missingLedgerForTxn}`} />
                <Chip label={`Orphan ledger: ${issueCounts.orphanLedger}`} />
                <Chip label={`Duplicate withdrawal groups: ${issueCounts.duplicates}`} />
              </Box>

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{ color: "#aaa" }}>
                    Totals from ledgers
                  </Typography>
                  <Typography sx={{ color: "#ccc" }}>
                    Deposits: {report.totals.ledger.deposits.count} · {formatMoney(report.totals.ledger.deposits.sum)}
                  </Typography>
                  <Typography sx={{ color: "#ccc" }}>
                    Withdrawals: {report.totals.ledger.withdrawals.count} · {formatMoney(report.totals.ledger.withdrawals.sum)}
                  </Typography>
                  <Typography sx={{ color: "#ccc" }}>
                    Performance: {report.totals.ledger.performance.count} · {formatMoney(report.totals.ledger.performance.sum)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{ color: "#aaa" }}>
                    Totals from transactions
                  </Typography>
                  <Typography sx={{ color: "#ccc" }}>
                    Deposits (Approved): {report.totals.txn.depositsApproved.count} · {formatMoney(report.totals.txn.depositsApproved.sum)}
                  </Typography>
                  <Typography sx={{ color: "#ccc" }}>
                    Withdrawals (Paid): {report.totals.txn.withdrawalsPaid.count} · {formatMoney(report.totals.txn.withdrawalsPaid.sum)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {report.issues.duplicates.length > 0 && (
            <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
                  Duplicate withdrawal ledgers (same amount + prev/curr balance)
                </Typography>
                {report.issues.duplicates.map((g) => (
                  <Box key={g.key} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
                      {g.key} · count {g.count}
                    </Typography>
                    <TableContainer component={Paper} sx={{ bgcolor: "transparent" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: "#aaa" }}>Ledger ID</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>doc_id</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Date</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Amount</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Prev</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Curr</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Status</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Migrated</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.rows.map((r) => (
                            <TableRow key={r.ledgerId}>
                              <TableCell sx={{ color: "#ccc", fontFamily: "monospace", fontSize: "0.75rem" }}>{r.ledgerId}</TableCell>
                              <TableCell sx={{ color: "#ccc", fontFamily: "monospace", fontSize: "0.75rem" }}>{r.doc_id || "—"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.when ? r.when.split("T")[0] : "—"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{formatMoney(r.amount)}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{formatMoney(r.prev)}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{formatMoney(r.curr)}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.status ?? "N/A"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.migrated_from || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {(report.issues.missingLedgerForTxn.length > 0 || report.issues.orphanLedger.length > 0) && (
            <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
                  Link issues
                </Typography>

                {report.issues.missingLedgerForTxn.length > 0 && (
                  <>
                    <Typography variant="overline" sx={{ color: "#aaa" }}>
                      Transactions that should have a ledger (missing/weak match)
                    </Typography>
                    <TableContainer component={Paper} sx={{ bgcolor: "transparent", mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: "#aaa" }}>Txn ID</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Type</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Status</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Date</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Amount</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.issues.missingLedgerForTxn.map((r) => (
                            <TableRow key={r.txnId}>
                              <TableCell sx={{ color: "#ccc", fontFamily: "monospace", fontSize: "0.75rem" }}>{r.txnId}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.type === 0 ? "Deposit" : "Withdrawal"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.status}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.when ? r.when.split("T")[0] : "—"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{formatMoney(r.amount)}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {report.issues.orphanLedger.length > 0 && (
                  <>
                    <Typography variant="overline" sx={{ color: "#aaa" }}>
                      Ledgers that should map to a transaction (missing/weak match)
                    </Typography>
                    <TableContainer component={Paper} sx={{ bgcolor: "transparent" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: "#aaa" }}>Ledger ID</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Type</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Date</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Amount</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>doc_id</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.issues.orphanLedger.map((r) => (
                            <TableRow key={r.ledgerId}>
                              <TableCell sx={{ color: "#ccc", fontFamily: "monospace", fontSize: "0.75rem" }}>{r.ledgerId}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.type === 0 ? "Deposit" : "Withdrawal"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.when ? r.when.split("T")[0] : "—"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{formatMoney(r.amount)}</TableCell>
                              <TableCell sx={{ color: "#ccc", fontFamily: "monospace", fontSize: "0.75rem" }}>{r.doc_id || "—"}</TableCell>
                              <TableCell sx={{ color: "#ccc" }}>{r.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

